import type { ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Brand } from './Brand';
import { UserMenu } from './UserMenu';
import { NotificationBell } from './NotificationBell';
import './AppShell.css';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const NAV_ICONS: Record<string, ReactNode> = {
  grid: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
  card: <><rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20" /></>,
  list: <path d="M8 6h11M8 12h11M8 18h11M3.5 6h.01M3.5 12h.01M3.5 18h.01" />,
  users: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0M17 11a3 3 0 0 0 0-6M20.5 20a5.5 5.5 0 0 0-4-5.3" /></>,
  staff: <><circle cx="12" cy="8" r="3.4" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /><path d="M12 2v1.5M12 12.5V14" /></>,
  bank: <path d="M4 10 12 4l8 6M6 10v9h12v-9M9 19v-5h6v5" />,
  contract: <><path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.15-1.4l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2.4-1.4L13.8 2h-3.6l-.4 2.3a7 7 0 0 0-2.4 1.4l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12a7 7 0 0 0 .15 1.4l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2.4 1.4l.4 2.3h3.6l.4-2.3a7 7 0 0 0 2.4-1.4l2.3 1 2-3.4-2-1.5A7 7 0 0 0 19 12Z" /></>,
  plus: <><rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20M12 13v3M10.5 14.5h3" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.2a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.2-2.5 3.9" /><path d="M12 17.2h.01" /></>,
};

function NavIco({ name }: { name: string }) {
  return (
    <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {NAV_ICONS[name] ?? NAV_ICONS.grid}
    </svg>
  );
}

const ADMIN_NAV: NavItem[] = [
  { to: '/panel', label: 'Genel Bakış', icon: 'grid' },
  { to: '/panel/odemeler', label: 'Ödeme Yönetimi', icon: 'list' },
  { to: '/panel/bayiler', label: 'Bayi Yönetimi', icon: 'users' },
  { to: '/panel/kullanicilar', label: 'Kullanıcı Yönetimi', icon: 'staff' },
  { to: '/panel/bankalar', label: 'Banka Ayarları', icon: 'bank' },
  { to: '/panel/ayarlar', label: 'Site Ayarları', icon: 'gear' },
  { to: '/panel/yardim', label: 'Yardım', icon: 'help' },
];

const MANAGER_NAV: NavItem[] = [
  { to: '/panel', label: 'Genel Bakış', icon: 'grid' },
  { to: '/panel/odemeler', label: 'Ödeme Yönetimi', icon: 'list' },
  { to: '/panel/bayiler', label: 'Bayi Yönetimi', icon: 'users' },
  { to: '/panel/yardim', label: 'Yardım', icon: 'help' },
];

const BAYI_NAV: NavItem[] = [
  { to: '/bayi', label: 'Genel Bakış', icon: 'grid' },
  { to: '/odeme', label: 'Ödeme Yap', icon: 'plus' },
  { to: '/odemelerim', label: 'Ödemelerim', icon: 'list' },
  { to: '/sozlesmeler', label: 'Sözleşmeler', icon: 'contract' },
];

export function AppShell() {
  const { user, impersonating, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === 'bayi' ? BAYI_NAV : user?.role === 'manager' ? MANAGER_NAV : ADMIN_NAV;
  const roleLabel = user?.role === 'bayi' ? 'Bayi' : user?.role === 'manager' ? 'Manager' : 'Yönetici';

  const handleStopImpersonation = async () => {
    await stopImpersonation();
    navigate('/panel', { replace: true });
  };

  return (
    <div className="shell">
      <aside className="shell-side">
        <Brand variant="shell" />
        <nav className="shell-nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
              end
            >
              <NavIco name={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="shell-side-foot">
          <div className="who">
            <div className="who-avatar">{(user?.full_name || user?.email || '?').slice(0, 2).toUpperCase()}</div>
            <div className="who-meta">
              <div className="who-name">{user?.full_name || user?.email}</div>
              <div className="who-role">{roleLabel}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="shell-body">
        {impersonating && (
          <div className="impersonation-bar">
            <span>
              🔒 <strong>{user?.company_name || user?.full_name || user?.email}</strong> bayisi olarak
              görüntülüyorsunuz.
            </span>
            <button className="impersonation-exit" onClick={handleStopImpersonation}>
              Yöneticiye Dön
            </button>
          </div>
        )}
        <header className="shell-top">
          <span style={{ flex: 1 }} />
          {user?.role !== 'bayi' && <NotificationBell />}
          <UserMenu />
        </header>
        <main className="shell-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
